import { useEffect, useState, lazy, Suspense } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import FieldRecordDialog, { type FieldRecordKind } from "@/components/FieldRecordDialog";
import WindrowPlanPanel from "@/components/WindrowPlanPanel";
import StagingMixPanel from "@/components/StagingMixPanel";
import { apiFetch } from "@/lib/api-fetch";
// @ts-ignore Canonical series helper is shared with node tests.
import { temperatureHistoryDomain, temperatureHistoryPoints } from "../../../../shared/temperature-history.mjs";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from "recharts";
import {
  ArrowLeft,
  Thermometer,
  RotateCcw,
  Package,
  CheckCircle2,
  AlertTriangle,
  Droplets,
  MapPin,
  Camera,
  X,
  Pencil,
  Printer,
} from "lucide-react";
const WindrowLinePicker = lazy(() => import("@/components/WindrowLinePicker"));
const WindrowCirclePicker = lazy(() => import("@/components/WindrowCirclePicker"));

type WindrowDetail = {
  windrow: {
    id: number;
    row_id: string;
    name: string;
    location: string | null;
    status: string;
    start_date: string;
    updated_at?: string;
    shape?: "line" | "pile";
    yard_role?: "cooking" | "staging" | "curing";
    length: number | null;
    width: number | null;
    cubic_yards: number | null;
    tonnage: number | null;
    recipe: string | null;
    notes: string | null;
    latitude: number | null;
    longitude: number | null;
    end_latitude: number | null;
    end_longitude: number | null;
    color: string | null;
  };
  temperatures: Array<{
    id: number;
    temperature: number;
    probe_location: string | null;
    photo_url: string | null;
    notes: string | null;
    recorded_by: string | null;
    recorded_at: string;
  }>;
  turns: Array<{
    id: number;
    turn_number: number;
    water_added: boolean;
    equipment: string | null;
    operator: string | null;
    photo_url: string | null;
    notes: string | null;
    turned_at: string;
  }>;
  materials: Array<{
    id: number;
    material_name: string;
    cubic_yards: number | null;
    weight: number | null;
    notes: string | null;
    added_at: string;
  }>;
  compliance: {
    consecutive_days: number;
    required_days: number;
    min_temp: number;
  };
};

const STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  active: { label: "Active", color: "bg-forest-100 text-forest-700 dark:bg-forest-900/30 dark:text-forest-400" },
  cooking_complete: { label: "Cooking Complete", color: "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400" },
  ready_for_pickup: { label: "Ready for Pickup", color: "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400" },
  removed: { label: "Removed", color: "bg-gray-100 text-gray-700 dark:bg-gray-900/30 dark:text-gray-400" },
};

export default function WindrowDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { token } = useAuth();
  const [detailsExpanded,setDetailsExpanded]=useState(false);
  useEffect(()=>{if(detailsExpanded){const panel=document.getElementById("full-windrow-details");panel?.scrollIntoView({behavior:"smooth",block:"start"});}},[detailsExpanded]);
  const isLocalDemo = import.meta.env.VITE_LOCAL_DEMO === "true";
  const navigate = useNavigate();
  const [data, setData] = useState<WindrowDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<"temps" | "turns" | "materials" | "photos">("temps");
  const [lightboxUrl, setLightboxUrl] = useState<string | null>(null);

  const [recordDetail, setRecordDetail] = useState<{ kind: FieldRecordKind; id: number } | null>(null);

  // Edit state
  const [showEdit, setShowEdit] = useState(false);
  const [saving, setSaving] = useState(false);
  const [allWindrows, setAllWindrows] = useState<Array<{
    id: number;
    name: string;
    latitude: number | null;
    longitude: number | null;
    end_latitude: number | null;
    end_longitude: number | null;
    shape?: "line" | "pile";
  }>>([]);
  const [editForm, setEditForm] = useState({
    name: "", rowId: "", location: "", status: "active",
    length: "", width: "", cubicYards: "", tonnage: "", recipe: "", notes: "",
    latitude: null as number | null, longitude: null as number | null,
    endLatitude: null as number | null, endLongitude: null as number | null,
    color: null as string | null,
  });

  const printLabel = () => {
    if (!data) return;
    const w = data.windrow;
    const win = window.open("", "_blank");
    if (!win) return;
    // Split name into words — each word must stay whole (no mid-letter breaks)
    const nameWords = (w.name || "").toUpperCase().split(/\s+/);
    win.document.write(`<!DOCTYPE html>
<html><head><title>${w.name} Label</title>
<style>
  @page { size: landscape; margin: 0; }
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body {
    width: 11in; height: 8.5in;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    font-family: 'Arial Black', 'Impact', 'Helvetica Neue', Arial, sans-serif;
    background: #fff; color: #1a1a1a;
    padding: 0.5in;
    overflow: hidden;
  }
  .name {
    font-weight: 900;
    letter-spacing: -3px; line-height: 0.95;
    text-align: center; text-transform: uppercase;
    white-space: nowrap;
    max-width: 10in;
  }
  .name-line {
    display: block;
    white-space: nowrap;
  }
  .row-id {
    font-size: 96px; font-weight: 900;
    color: #444; letter-spacing: 2px;
    margin-top: 20px; text-align: center;
  }
  .meta {
    font-size: 28px; font-weight: 700;
    color: #888; margin-top: 24px;
    text-align: center; letter-spacing: 1px;
  }
</style></head><body>
<div class="name" id="nameEl">${nameWords.map(word => `<span class="name-line">${word}</span>`).join("")}</div>
<div class="row-id">${w.row_id}</div>
${w.recipe ? `<div class="meta">${w.recipe}</div>` : ""}
<script>
(function(){
  var el = document.getElementById("nameEl");
  var lines = el.querySelectorAll(".name-line");
  var maxW = 10 * 96; /* 10in in px at 96dpi */
  /* Start big, shrink until every word fits on one line */
  var sz = 220;
  el.style.fontSize = sz + "px";
  function anyOverflow() {
    for (var i = 0; i < lines.length; i++) {
      if (lines[i].scrollWidth > maxW) return true;
    }
    return false;
  }
  while (sz > 40 && anyOverflow()) {
    sz -= 4;
    el.style.fontSize = sz + "px";
  }
  window.print();
})();
<\/script>
</body></html>`);
    win.document.close();
  };

  const openEdit = async () => {
    if (!data) return;
    const w = data.windrow;
    setEditForm({
      name: w.name || "", rowId: w.row_id || "", location: w.location || "", status: w.status || "active",
      length: w.length?.toString() || "", width: w.width?.toString() || "12",
      cubicYards: w.cubic_yards?.toString() || "", tonnage: w.tonnage?.toString() || "", recipe: w.recipe || "", notes: w.notes || "",
      latitude: w.latitude ?? null, longitude: w.longitude ?? null,
      endLatitude: w.end_latitude ?? null,
      endLongitude: w.end_longitude ?? null,
      color: w.color ?? null,
    });
    setShowEdit(true);
    try {
      const res = await apiFetch("/api/windrows", { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const json = await res.json();
        const list = (json.windrows || []).map((row: { id: number; name?: string; latitude?: number | null; longitude?: number | null; end_latitude?: number | null; end_longitude?: number | null; shape?: "line" | "pile" }) => ({
          id: row.id,
          name: row.name ?? "",
          latitude: row.latitude ?? null,
          longitude: row.longitude ?? null,
          end_latitude: row.end_latitude ?? null,
          end_longitude: row.end_longitude ?? null,
          shape: row.shape,
        }));
        setAllWindrows(list);
      }
    } catch {
      setAllWindrows([]);
    }
  };

  const handleEditSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data) return;
    setSaving(true);
    try {
      const res = await apiFetch(`/api/windrows/${data.windrow.id}`, {
        method: "PATCH",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({
          name: editForm.name || undefined,
          rowId: editForm.rowId || undefined,
          location: editForm.location || undefined,
          status: editForm.status || undefined,
          ...(!isLocalDemo ? {
          length: editForm.length ? Number(editForm.length) : undefined,
          width: editForm.width ? Number(editForm.width) : undefined,
          cubicYards: editForm.cubicYards ? Number(editForm.cubicYards) : undefined,
          tonnage: editForm.tonnage ? Number(editForm.tonnage) : undefined,
          recipe: editForm.recipe || undefined,
          } : {}),
          notes: editForm.notes,
          latitude: editForm.latitude,
          longitude: editForm.longitude,
          end_latitude: editForm.endLatitude,
          end_longitude: editForm.endLongitude,
          color: editForm.color || undefined,
        }),
      });
      if (!res.ok) throw new Error("Failed to update windrow");
      const updated = await res.json();
      setData({ ...data, windrow: updated.windrow });
      setShowEdit(false);
    } catch (err) {
      alert("Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    apiFetch(`/api/windrows/${id}`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => {
        if (!r.ok) throw new Error("Not found");
        return r.json();
      })
      .then(setData)
      .catch(() => navigate("/app/windrows"))
      .finally(() => setLoading(false));
  }, [id, token, navigate]);

  useEffect(() => {
    if (!isLocalDemo || showEdit) return;
    let alive = true;
    const refresh = () => {
      if (document.visibilityState !== "visible" || document.querySelector('[role="dialog"]')) return;
      apiFetch(`/api/windrows/${id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null).then(next => { if (alive && next) setData(next); }).catch(() => {});
    };
    const timer = window.setInterval(refresh, 3000);
    window.addEventListener("focus", refresh);
    return () => { alive = false; clearInterval(timer); window.removeEventListener("focus", refresh); };
  }, [id, token, isLocalDemo, showEdit]);

  if (loading) {
    return (
      <div className="space-y-6 max-w-6xl">
        <div className="h-8 bg-muted rounded w-48 animate-pulse" />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="bg-card border border-border rounded-xl p-5 animate-pulse">
              <div className="h-3 bg-muted rounded w-20 mb-4" />
              <div className="h-9 bg-muted rounded w-16" />
            </div>
          ))}
        </div>
        <div className="h-72 bg-card border border-border rounded-xl animate-pulse" />
      </div>
    );
  }

  if (!data) return null;

  const { windrow, temperatures, turns, materials } = data;
  const statusConfig = STATUS_CONFIG[windrow.status] || STATUS_CONFIG.active;
  const isStaging = (windrow.yard_role || "cooking") === "staging";
  const lastTemp = temperatures.length > 0 ? temperatures[temperatures.length - 1] : null;

  const chartData = temperatureHistoryPoints(temperatures);

  // Collect all photos from temps and turns
  const allPhotos = [
    ...temperatures.filter((t) => t.photo_url).map((t) => ({
      url: t.photo_url!,
      type: "temp" as const,
      label: `${t.temperature}°F${t.probe_location ? ` (${t.probe_location})` : ""}`,
      date: t.recorded_at,
      by: t.recorded_by,
    })),
    ...turns.filter((t) => t.photo_url).map((t) => ({
      url: t.photo_url!,
      type: "turn" as const,
      label: `Turn #${t.turn_number}${t.equipment ? ` — ${t.equipment}` : ""}`,
      date: t.turned_at,
      by: t.operator,
    })),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());

  return (
    <div className="space-y-5 max-w-7xl">
      {recordDetail && <FieldRecordDialog kind={recordDetail.kind} recordId={recordDetail.id} onClose={() => setRecordDetail(null)} onSaved={() => { apiFetch(`/api/windrows/${id}`, {headers:{Authorization:`Bearer ${token}`}}).then(r => r.ok ? r.json() : null).then(next => {if(next) setData(next);}).catch(() => {}); }} />}
      <header>
        <Link to="/app/windrows" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-2">
          <ArrowLeft size={13} /> Windrows and piles
        </Link>
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-x-3 gap-y-1 flex-wrap min-w-0">
            <h1 className="heading-display text-2xl sm:text-3xl text-foreground">{windrow.name}</h1>
            <span className="text-xs text-muted-foreground font-mono">{windrow.row_id}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold ${statusConfig.color}`}>{statusConfig.label}</span>
            {isStaging && <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold bg-earth-500/15 text-earth-500">Staging</span>}
          </div>
          <Link to={`/app/log-temp?windrow=${windrow.id}`} className="lg:hidden shrink-0 inline-flex items-center gap-1.5 px-3 py-2 bg-forest-600 text-white text-xs font-semibold rounded-lg">
            <Thermometer size={14} /> Log temp
          </Link>
        </div>
        <p className="text-xs text-muted-foreground mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          {windrow.location && <span className="inline-flex items-center gap-1"><MapPin size={12} />{windrow.location}</span>}
          <span>Started {new Date(windrow.start_date).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</span>
        </p>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_240px] gap-5 items-start">
      <div className="min-w-0 space-y-4">
      {isStaging && <StagingMixPanel windrowId={windrow.id} onMixed={() => {
        apiFetch(`/api/windrows/${id}`, {headers:{Authorization:`Bearer ${token}`}}).then(r => r.ok ? r.json() : null).then(next => {if(next) setData(next);}).catch(() => {});
      }} />}
      {/* Temperature chart */}
      <div className="bg-card border border-border rounded-xl px-3 pt-4 pb-2 sm:px-4">
        <div className="flex items-center justify-between gap-3 mb-3 px-1">
          <h2 className="heading-display text-lg text-foreground">Temperature History</h2>
          <span className="text-xs text-muted-foreground">{temperatures.length} readings</span>
        </div>
        {chartData.length > 0 ? (
          <ResponsiveContainer width="100%" height={210}>
            <LineChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" className="opacity-20" />
              <XAxis
                dataKey="at"
                type="number"
                domain={temperatureHistoryDomain(chartData)}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(value) => new Date(Number(value)).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
              />
              <YAxis
                domain={[
                  (min: number) => Math.min(80, Number.isFinite(min) ? min - 5 : 80),
                  (max: number) => Math.max(180, Number.isFinite(max) ? max + 5 : 180),
                ]}
                tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }}
                tickFormatter={(v) => `${v}°`}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "hsl(var(--card))",
                  border: "1px solid hsl(var(--border))",
                  borderRadius: "12px",
                  fontSize: "12px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                }}
                labelFormatter={(value) => {
                  const at = Number(value);
                  return Number.isFinite(at) ? new Date(at).toLocaleString() : "";
                }}
                formatter={(value: number | string | undefined) => {
                  const v = typeof value === "number" ? value : Number(value)
                  return [`${Number.isFinite(v) ? v : ""}°F`, "Temperature"]
                }}
              />
              <ReferenceLine
                y={131}
                stroke="#ef4444"
                strokeDasharray="5 5"
                label={{
                  value: "131°F reference",
                  position: "insideTopRight",
                  fill: "#ef4444",
                  fontSize: 11,
                }}
              />
              <Line
                type="monotone"
                dataKey="temp"
                stroke="#3d8f3d"
                strokeWidth={2.5}
                isAnimationActive={false}
                dot={{ fill: "#3d8f3d", r: 3, strokeWidth: 0 }}
                activeDot={{ r: 6, strokeWidth: 2, stroke: "#fff" }}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-48 flex items-center justify-center">
            <div className="text-center">
              <Thermometer size={28} className="mx-auto text-muted-foreground/30 mb-2" />
              <p className="text-sm text-muted-foreground">No temperature readings yet</p>
            </div>
          </div>
        )}
      </div>

      {/* Record types */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex gap-1 bg-secondary/80 rounded-xl p-1 flex-1 min-w-0">
          {[
            { key: "temps" as const, label: "Temperatures", count: temperatures.length, icon: Thermometer },
            { key: "turns" as const, label: "Turns", count: turns.length, icon: RotateCcw },
            { key: "materials" as const, label: "Materials", count: materials.length, icon: Package },
            { key: "photos" as const, label: "Photos", count: allPhotos.length, icon: Camera },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              aria-label={`${t.label} (${t.count})`}
              aria-pressed={tab === t.key}
              className={`flex-1 min-w-0 inline-flex items-center justify-center gap-2 px-3 py-2.5 text-sm font-semibold rounded-lg transition-all ${
                tab === t.key
                  ? "bg-card text-foreground shadow-sm"
                  : "text-muted-foreground hover:text-foreground"
              }`}
            >
              <t.icon size={14} className="shrink-0" />
              <span className="hidden sm:inline truncate">{t.label}</span>
              <span className={`text-xs shrink-0 ${tab === t.key ? "text-muted-foreground" : "text-muted-foreground/50"}`}>
                ({t.count})
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Tab content */}
      <div className="bg-card border border-border rounded-xl overflow-hidden">
        {tab === "temps" && (
          <div className="divide-y divide-border">
            <div className="px-4 py-2.5 text-xs text-muted-foreground bg-secondary/20">Most recent first · all temperatures in °F</div>
            {temperatures.length === 0 ? (
              <div className="p-12 text-center">
                <Thermometer size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">No temperature readings yet</p>
                <Link to={`/app/log-temp?windrow=${windrow.id}`} className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-lg transition-colors">
                  <Thermometer size={14} />
                  Log first temperature
                </Link>
              </div>
            ) : (
              [...temperatures].reverse().map((t) => (
                <div key={t.id} className="p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${
                      t.temperature < 131
                        ? "bg-red-100 dark:bg-red-900/30"
                        : "bg-forest-100 dark:bg-forest-900/30"
                    }`}>
                      <Thermometer size={15} className={t.temperature < 131 ? "text-red-500 dark:text-red-400" : "text-forest-600 dark:text-forest-400"} />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        <span className="stat-value">{t.temperature}°F</span>
                        {t.probe_location && (
                          <span className="text-muted-foreground font-normal text-xs ml-2 capitalize">({t.probe_location})</span>
                        )}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t.recorded_by && <span className="font-medium">{t.recorded_by}</span>}
                        {t.recorded_by && " · "}
                        {new Date(t.recorded_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setRecordDetail({kind:"temperature",id:t.id})} className="min-h-11 px-3 rounded-lg border border-border text-xs font-semibold" aria-label={`View temperature ${t.temperature} degrees details`}>Details</button>
                    {t.photo_url && (
                      <button onClick={() => setLightboxUrl(t.photo_url)} className="w-9 h-9 rounded-lg overflow-hidden border border-border hover:border-forest-300 transition-colors">
                        <img src={t.photo_url} alt="" className="w-full h-full object-cover" />
                      </button>
                    )}
                    {t.temperature < 131 && (
                      <span className="text-[10px] text-red-500 font-semibold uppercase tracking-wide flex items-center gap-1">
                        <AlertTriangle size={10} />
                        Below min
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "turns" && (
          <div className="divide-y divide-border">
            <div className="p-3 flex items-center justify-between gap-3 border-b border-border bg-secondary/30">
              <p className="text-xs text-muted-foreground">Record a turn event for this pile.</p>
              <Link
                to={`/app/log-turn?windrow=${windrow.id}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors shrink-0"
              >
                <RotateCcw size={14} />
                Log turn
              </Link>
            </div>
            {turns.length === 0 ? (
              <div className="p-12 text-center">
                <RotateCcw size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">No turns recorded yet</p>
                <Link to={`/app/log-turn?windrow=${windrow.id}`} className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
                  <RotateCcw size={14} />
                  Log first turn
                </Link>
              </div>
            ) : (
              turns.map((t) => (
                <div key={t.id} className="p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <RotateCcw size={15} className="text-blue-600 dark:text-blue-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">
                        Turn #{t.turn_number}
                        {t.equipment && <span className="text-muted-foreground font-normal text-xs ml-2">({t.equipment})</span>}
                      </p>
                      <p className="text-xs text-muted-foreground">
                        {t.operator && <span className="font-medium">{t.operator}</span>}
                        {t.operator && " · "}
                        {new Date(t.turned_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={() => setRecordDetail({kind:"turn",id:t.id})} className="min-h-11 px-3 rounded-lg border border-border text-xs font-semibold" aria-label={`View turn ${t.turn_number} details`}>Details</button>
                    {t.photo_url && (
                      <button onClick={() => setLightboxUrl(t.photo_url)} className="w-9 h-9 rounded-lg overflow-hidden border border-border hover:border-blue-300 transition-colors">
                        <img src={t.photo_url} alt="" className="w-full h-full object-cover" />
                      </button>
                    )}
                    {t.water_added && (
                      <span className="inline-flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 font-semibold bg-blue-100 dark:bg-blue-900/30 px-2 py-1 rounded-full">
                        <Droplets size={11} />
                        Water
                      </span>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "materials" && (
          <div className="divide-y divide-border">
            <div className="p-3 flex items-center justify-between gap-3 border-b border-border bg-secondary/30">
              <p className="text-xs text-muted-foreground">Record materials added to this pile.</p>
              <Link
                to={`/app/log-material?windrow=${windrow.id}`}
                className="inline-flex items-center gap-2 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors shrink-0"
              >
                <Package size={14} />
                Add material
              </Link>
            </div>
            {materials.length === 0 ? (
              <div className="p-12 text-center">
                <Package size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">No materials recorded yet</p>
                <Link to={`/app/log-material?windrow=${windrow.id}`} className="inline-flex items-center gap-2 mt-3 px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold rounded-lg transition-colors">
                  <Package size={14} />
                  Add first material
                </Link>
              </div>
            ) : (
              materials.map((m) => (
                <div key={m.id} className="p-4 flex items-center justify-between hover:bg-secondary/30 transition-colors">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                      <Package size={15} className="text-amber-600 dark:text-amber-400" />
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-foreground">{m.material_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(m.added_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}
                        {m.notes && ` · ${m.notes}`}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <button onClick={() => setRecordDetail({kind:"material",id:m.id})} className="min-h-11 px-3 rounded-lg border border-border text-xs font-semibold" aria-label={`View ${m.material_name} details`}>Details</button>
                    {m.cubic_yards && <p className="stat-value text-sm text-foreground">{m.cubic_yards} yd³</p>}
                    {m.weight && <p className="text-xs text-muted-foreground">{m.weight} tons</p>}
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {tab === "photos" && (
          <div>
            <div className="p-3 flex flex-wrap items-center justify-between gap-3 border-b border-border bg-secondary/30">
              <p className="text-xs text-muted-foreground">Photos are added when you log a temperature or turn with a photo.</p>
              <div className="flex flex-wrap gap-2 shrink-0">
                <Link
                  to={`/app/log-temp?windrow=${windrow.id}`}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  <Thermometer size={14} />
                  Log temp (with photo)
                </Link>
                <Link
                  to={`/app/log-turn?windrow=${windrow.id}`}
                  className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors"
                >
                  <RotateCcw size={14} />
                  Log turn (with photo)
                </Link>
              </div>
            </div>
            {allPhotos.length === 0 ? (
              <div className="p-12 text-center">
                <Camera size={32} className="mx-auto text-muted-foreground/30 mb-3" />
                <p className="text-sm text-muted-foreground">No photos captured yet</p>
                <p className="text-xs text-muted-foreground mt-1">Add a photo by logging a temperature or turn and attaching an image.</p>
                <div className="flex flex-wrap gap-2 justify-center mt-4">
                  <Link to={`/app/log-temp?windrow=${windrow.id}`} className="inline-flex items-center gap-2 px-4 py-2 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-lg transition-colors">
                    <Thermometer size={14} />
                    Log temperature
                  </Link>
                  <Link to={`/app/log-turn?windrow=${windrow.id}`} className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-lg transition-colors">
                    <RotateCcw size={14} />
                    Log turn
                  </Link>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-1 p-1">
                {allPhotos.map((photo, i) => (
                  <button
                    key={i}
                    onClick={() => setLightboxUrl(photo.url)}
                    className="relative group aspect-square overflow-hidden rounded-lg"
                  >
                    <img
                      src={photo.url}
                      alt={photo.label}
                      className="w-full h-full object-cover transition-transform group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    <div className="absolute bottom-0 left-0 right-0 p-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <p className="text-xs font-semibold text-white truncate">{photo.label}</p>
                      <p className="text-[10px] text-white/70">
                        {new Date(photo.date).toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                        {photo.by && ` · ${photo.by}`}
                      </p>
                    </div>
                    <div className={`absolute top-2 left-2 px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wider ${
                      photo.type === "temp"
                        ? "bg-amber-500/90 text-white"
                        : "bg-blue-500/90 text-white"
                    }`}>
                      {photo.type === "temp" ? "Temp" : "Turn"}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      </div>
      <aside className={`space-y-4 ${detailsExpanded?'':'lg:sticky lg:top-5'}`} aria-label="Windrow actions and details">
        <div className="bg-card border border-border rounded-xl p-3 space-y-1.5">
          <Link to={`/app/log-temp?windrow=${windrow.id}`} className="flex items-center gap-2 px-3 py-2.5 bg-forest-600 hover:bg-forest-700 text-white text-sm font-semibold rounded-lg transition-colors"><Thermometer size={16} /> Log temperature</Link>
          <Link to={`/app/log-turn?windrow=${windrow.id}`} className="flex items-center gap-2 px-3 py-2.5 hover:bg-secondary text-foreground text-sm rounded-lg transition-colors"><RotateCcw size={16} /> Log turn</Link>
          <Link to={`/app/log-material?windrow=${windrow.id}`} className="flex items-center gap-2 px-3 py-2.5 hover:bg-secondary text-foreground text-sm rounded-lg transition-colors"><Package size={16} /> Add material</Link>
          <div className="border-t border-border pt-1.5 flex gap-1">
            <button onClick={openEdit} className="flex-1 inline-flex justify-center items-center gap-1.5 px-2 py-2 hover:bg-secondary text-muted-foreground text-xs rounded-lg"><Pencil size={13} /> Edit</button>
            <button onClick={printLabel} className="flex-1 inline-flex justify-center items-center gap-1.5 px-2 py-2 hover:bg-secondary text-muted-foreground text-xs rounded-lg"><Printer size={13} /> Print label</button>
          </div>
        </div>
        <div className="bg-card border border-border rounded-xl p-4">
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-xs font-medium text-muted-foreground">Latest reading</h2>
            <span className="text-lg font-semibold tabular-nums text-foreground">{lastTemp ? `${lastTemp.temperature}°F` : "—"}</span>
          </div>
          {lastTemp && <p className="mt-1 text-xs text-muted-foreground">{new Date(lastTemp.recorded_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}{lastTemp.probe_location ? ` · ${lastTemp.probe_location}` : ""}</p>}
          {lastTemp?.recorded_by && <p className="mt-1 text-xs text-muted-foreground">{lastTemp.recorded_by}</p>}
          <dl className="mt-3 pt-3 border-t border-border flex justify-between text-xs">
            <div className="flex gap-1.5"><dt className="text-muted-foreground">Readings</dt><dd className="font-semibold">{temperatures.length}</dd></div>
            <div className="flex gap-1.5"><dt className="text-muted-foreground">Turns</dt><dd className="font-semibold">{turns.length}</dd></div>
          </dl>
        </div>
      {/* Windrow details */}
      <div className="bg-card border border-border rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h2 className="heading-display text-base text-foreground">Windrow Details</h2>
          <button onClick={openEdit} className="text-sm text-forest-600 dark:text-forest-400 hover:underline font-medium">
            Edit Details
          </button>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-1 gap-3 text-sm">
          {windrow.location && (
            <div>
              <p className="label-caps text-muted-foreground mb-1">Location</p>
              <p className="font-semibold text-foreground">{windrow.location}</p>
            </div>
          )}
          {windrow.cubic_yards && (
            <div>
              <p className="label-caps text-muted-foreground mb-1">Volume</p>
              <p className="font-semibold text-foreground">{windrow.cubic_yards} yd³</p>
            </div>
          )}
          {windrow.tonnage && (
            <div>
              <p className="label-caps text-muted-foreground mb-1">Tonnage</p>
              <p className="font-semibold text-foreground">{windrow.tonnage} US tons</p>
            </div>
          )}
          {windrow.length && (
            <div>
              <p className="label-caps text-muted-foreground mb-1">Dimensions</p>
              <p className="font-semibold text-foreground">{windrow.length}' x {windrow.width || 12}'</p>
            </div>
          )}
          {!isLocalDemo && windrow.recipe && (
            <div>
              <p className="label-caps text-muted-foreground mb-1">Recipe</p>
              <p className="font-semibold text-foreground">{windrow.recipe}</p>
            </div>
          )}
        </div>

      </div>

        <button type="button" aria-expanded={detailsExpanded} aria-controls="full-windrow-details" onClick={()=>setDetailsExpanded(v=>!v)} className="w-full bg-card border border-border rounded-xl p-4 text-left text-sm font-semibold text-forest-600">{detailsExpanded?'See fewer details':'See more details'}</button>
      </aside>
      {detailsExpanded && <section id="full-windrow-details" aria-label="Full windrow details" className="lg:col-span-2 bg-card border border-border rounded-xl p-4 sm:p-6 space-y-4 min-w-0">
        <h2 className="heading-display text-xl">Full windrow details</h2>
        <section><h3 className="font-semibold text-sm mb-2">Windrow notes</h3><p className="text-sm whitespace-pre-wrap break-words">{windrow.notes || 'No notes yet. Use Edit Details to add notes.'}</p></section>
        {isLocalDemo && <WindrowPlanPanel windrow={windrow} onUpdated={updated=>setData({...data,windrow:updated})}/>}
      </section>}
      </div>

      {/* Lightbox */}
      {lightboxUrl && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
        >
          <button
            onClick={() => setLightboxUrl(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
          >
            <X size={20} />
          </button>
          <img
            src={lightboxUrl}
            alt="Full size"
            className="max-w-full max-h-[90vh] object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}

      {/* Edit — full page for more space and accessibility */}
      {showEdit && (
        <div className="fixed inset-0 z-50 flex flex-col bg-background">
          <div className="flex items-center justify-between shrink-0 px-4 md:px-6 py-3 border-b border-border bg-card">
            <h2 className="text-xl font-bold text-foreground">Edit Windrow</h2>
            <button type="button" onClick={() => setShowEdit(false)} className="w-10 h-10 flex items-center justify-center rounded-xl hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors" aria-label="Close">
              <X size={20} />
            </button>
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto">
            <form onSubmit={handleEditSave} className="max-w-4xl mx-auto p-6 md:p-8 space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Name *</label>
                  <input type="text" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} required
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Row ID</label>
                  <input type="text" value={editForm.rowId} onChange={(e) => setEditForm({ ...editForm, rowId: e.target.value })}
                    className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Status</label>
                <select value={editForm.status} onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm appearance-none">
                  <option value="active">Active</option>
                  <option value="cooking_complete">Cooking Complete</option>
                  <option value="ready_for_pickup">Ready for Pickup</option>
                  <option value="removed">Removed</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Location</label>
                <input type="text" value={editForm.location} onChange={(e) => setEditForm({ ...editForm, location: e.target.value })} placeholder="e.g., East Row 1"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm" />
              </div>

              {!isLocalDemo && <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Length (ft)</label>
                  <input type="number" value={editForm.length} onChange={(e) => setEditForm({ ...editForm, length: e.target.value })} placeholder="150"
                    className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Width (ft)</label>
                  <input type="number" value={editForm.width} onChange={(e) => setEditForm({ ...editForm, width: e.target.value })} placeholder="12"
                    className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Volume (yd³)</label>
                  <input type="number" value={editForm.cubicYards} onChange={(e) => setEditForm({ ...editForm, cubicYards: e.target.value })} placeholder="200"
                    className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Tonnage</label>
                  <input type="number" step="0.1" value={editForm.tonnage} onChange={(e) => setEditForm({ ...editForm, tonnage: e.target.value })} placeholder="tons"
                    className="w-full px-3 py-2.5 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Recipe</label>
                <input type="text" value={editForm.recipe} onChange={(e) => setEditForm({ ...editForm, recipe: e.target.value })} placeholder="e.g., 60% green waste, 40% wood chips"
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm" />
              </div>

              </>}
              {isLocalDemo && <p className="text-sm text-muted-foreground">Use Target recipe → Edit on the windrow page to update its dimensions, quantities and ingredients together.</p>}
              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Notes</label>
                <textarea rows={3} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })} placeholder="Additional notes..."
                  className="w-full px-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm resize-none" />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">Map line color</label>
                <div className="flex flex-wrap gap-2">
                  {[
                    "#3d8f3d",
                    "#3b82f6",
                    "#8b5cf6",
                    "#9ca3af",
                    "#eab308",
                    "#ea580c",
                    "#dc2626",
                    "#0d9488",
                    "#6366f1",
                    "#ec4899",
                  ].map((hex) => (
                    <button
                      key={hex}
                      type="button"
                      onClick={() => setEditForm({ ...editForm, color: editForm.color === hex ? null : hex })}
                      className={`w-8 h-8 rounded-lg border-2 transition-all shrink-0 ${
                        editForm.color === hex
                          ? "border-gray-900 dark:border-white scale-110 ring-2 ring-offset-2 ring-gray-400 dark:ring-gray-500"
                          : "border-gray-300 dark:border-gray-600 hover:scale-105"
                      }`}
                      style={{ backgroundColor: hex }}
                      title={editForm.color === hex ? "Clear color (use status default)" : `Use ${hex}`}
                    />
                  ))}
                  {editForm.color && (
                    <span className="text-[10px] text-gray-500 dark:text-gray-400 self-center ml-1">Click selected to clear</span>
                  )}
                </div>
                <p className="text-[10px] text-gray-500 dark:text-gray-400 mt-1">Optional. Used for the windrow line and label on the map.</p>
              </div>

              <Suspense fallback={<div className="h-[280px] bg-gray-100 dark:bg-gray-800 rounded-lg animate-pulse" />}>
                {data.windrow.shape === "pile" ? (
                  <WindrowCirclePicker
                    latitude={editForm.latitude}
                    longitude={editForm.longitude}
                    endLatitude={editForm.endLatitude}
                    endLongitude={editForm.endLongitude}
                    onCircleChange={(centerLat, centerLng, edgeLat, edgeLng) =>
                      setEditForm({ ...editForm, latitude: centerLat, longitude: centerLng, endLatitude: edgeLat, endLongitude: edgeLng })
                    }
                    otherWindrows={allWindrows}
                    editingWindrowId={data.windrow.id}
                    editingWindrowName={data.windrow.name}
                    lineColor={editForm.color}
                  />
                ) : (
                  <WindrowLinePicker
                    latitude={editForm.latitude}
                    longitude={editForm.longitude}
                    endLatitude={editForm.endLatitude}
                    endLongitude={editForm.endLongitude}
                    onLineChange={(lat, lng, endLat, endLng) =>
                      setEditForm({ ...editForm, latitude: lat, longitude: lng, endLatitude: endLat, endLongitude: endLng })
                    }
                    otherWindrows={allWindrows}
                    editingWindrowId={data.windrow.id}
                    editingWindrowName={data.windrow.name}
                    lineColor={editForm.color}
                  />
                )}
              </Suspense>

              <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                <button type="submit" disabled={saving || !editForm.name.trim()}
                  className="min-w-[140px] px-5 py-3 bg-forest-600 hover:bg-forest-700 disabled:opacity-50 text-white rounded-xl text-sm font-semibold transition-colors">
                  {saving ? "Saving..." : "Save Changes"}
                </button>
                <button type="button" onClick={() => setShowEdit(false)}
                  className="min-w-[100px] px-5 py-3 bg-secondary hover:bg-secondary/80 text-foreground rounded-xl text-sm font-semibold transition-colors border border-border">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
